/**
 * fs-np-masonry.js
 * National Park (fs-np) "みんなが写した風景" masonry gallery.
 *
 * Initializes Desandro Masonry on each `.fs-np-masonry__grid`, relaying out as
 * images finish loading (imagesLoaded), and re-initializing in the Shopify theme
 * editor so merchant block add/remove/reorder is reflected immediately.
 *
 * The photo lightbox is a shared <dialog> holding a Swiper track (one slide per
 * photo); clicking a tile opens it at that photo. Prev/next is driven by keyboard
 * and swipe (Swiper) and the per-slide caption-row nav buttons (click delegation).
 *
 * Vanilla JS, jQuery-independent. Degrades gracefully: when Masonry is
 * unavailable the items stay in normal flow (CSS inline-block fallback); when
 * Swiper is unavailable the clicked photo still shows (CSS active-slide rule).
 */
(function () {
  'use strict';

  var GRID_SELECTOR = '.fs-np-masonry__grid';
  var SECTION_SELECTOR = '.fs-np-masonry';
  var MODAL_SELECTOR = '.fs-np-masonry__modal';
  var MODAL_SWIPER_SELECTOR = '.fs-np-masonry__modal-swiper';
  var TRIGGER_SELECTOR = '[data-fs-np-modal-trigger]';
  var SLIDE_SELECTOR = '.fs-np-masonry__modal-slide';
  var ACTIVE_CLASS = 'fs-np-masonry__modal-slide--active';
  var IMAGE_SELECTOR = '.fs-np-masonry__modal-image';
  var PREV_SELECTOR = '[data-fs-np-modal-prev]';
  var NEXT_SELECTOR = '[data-fs-np-modal-next]';
  var NAV_SELECTOR = PREV_SELECTOR + ', ' + NEXT_SELECTOR;
  var INSTANCE_KEY = 'fsNpMasonry';
  var MODAL_BOUND_KEY = 'fsNpMasonryModalBound';
  var SWIPER_INSTANCE_KEY = 'fsNpMasonrySwiper';
  // How many slides on each side of the active one get their image realized
  // (see loadSlidesAround) — enough to survive one keyboard/swipe step without
  // a flash of the not-yet-loaded next photo, not so many it re-creates the
  // fade effect's all-58-at-once problem this exists to avoid.
  var LAZY_RADIUS = 1;

  var MASONRY_OPTIONS = {
    itemSelector: '.fs-np-masonry__item',
    columnWidth: '.fs-np-masonry__sizer',
    gutter: '.fs-np-masonry__gutter',
    percentPosition: true,
    horizontalOrder: true, // keep merchant entry order (mostly) left-to-right
    transitionDuration: 0,
  };

  function initGrid(grid) {
    if (!grid || typeof window.Masonry !== 'function') return;
    if (grid[INSTANCE_KEY]) return; // already initialized

    var instance = new window.Masonry(grid, MASONRY_OPTIONS);
    grid[INSTANCE_KEY] = instance;

    // Relayout as each image resolves so heights settle correctly.
    if (typeof window.imagesLoaded === 'function') {
      window.imagesLoaded(grid).on('progress', function () {
        instance.layout();
      });
    }
  }

  function destroyGrid(grid) {
    if (grid && grid[INSTANCE_KEY]) {
      grid[INSTANCE_KEY].destroy();
      grid[INSTANCE_KEY] = null;
    }
  }

  function layoutGrid(grid) {
    if (grid && grid[INSTANCE_KEY]) {
      grid[INSTANCE_KEY].layout();
    }
  }

  function forEachGrid(root, callback) {
    if (!root) return;
    var scope = root.querySelectorAll ? root : document;
    var grids = scope.querySelectorAll(GRID_SELECTOR);
    for (var i = 0; i < grids.length; i++) callback(grids[i]);
    // A section-load target may itself be a grid wrapper.
    if (root.matches && root.matches(GRID_SELECTOR)) callback(root);
  }

  function forEachSection(root, callback) {
    if (!root) return;
    var scope = root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) callback(sections[i]);
    // A section-load target may itself be the masonry section root.
    if (root.matches && root.matches(SECTION_SELECTOR)) callback(root);
  }

  function prefersReducedMotion() {
    // Mirrors fs-np-hero.js's helper of the same name.
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // ---- Photo lightbox modal ---------------------------------------------------
  // One shared dialog per section holding a Swiper track of pre-rendered slides
  // (one per photo, from the Liquid). Clicking a grid trigger opens the dialog at
  // that photo's index; prev/next is driven by keyboard and swipe (Swiper) and the
  // per-slide caption-row nav buttons (click delegation, see initModal). Independent
  // of Masonry: the modal works even if the Masonry lib failed. Swiper is created
  // lazily on first open (by which point swiper-bundle.js has run, even though it
  // loads after this file).

  function setActiveSlide(dialog, index) {
    // Setting inert on a slide blurs it if it contains the focused element (per
    // the inert spec). The caption-row nav buttons live inside their own slide,
    // so a keyboard press on the visible Next/Prev button would otherwise strand
    // focus at the dialog's first focusable element after every navigation.
    // Capture which nav button (if any) is focused before reassigning inert,
    // then restore focus to its counterpart on the new active slide below.
    var focused = document.activeElement && document.activeElement.closest
      ? document.activeElement.closest(NAV_SELECTOR)
      : null;
    var focusedNavSelector = focused && dialog.contains(focused)
      ? (focused.matches(PREV_SELECTOR) ? PREV_SELECTOR : NEXT_SELECTOR)
      : null;

    // Runs regardless of Swiper: on a Swiper load failure this still surfaces the
    // CLICKED photo (paired with the :not(.swiper-initialized) CSS rule), rather
    // than defaulting to the first slide the way the hero's fallback does.
    var slides = dialog.querySelectorAll(SLIDE_SELECTOR);
    for (var i = 0; i < slides.length; i++) {
      var isActive = i === index;
      slides[i].classList.toggle(ACTIVE_CLASS, isActive);
      // Swiper only transforms off-screen slides out of view; it doesn't remove
      // them from the tab order. Without this, Tab inside the focus-trapped
      // dialog reaches every other slide's Instagram link (58 stops, not 1).
      slides[i].inert = !isActive;
    }

    if (focusedNavSelector) {
      var nextFocus = slides[index] && slides[index].querySelector(focusedNavSelector);
      if (nextFocus) nextFocus.focus({ preventScroll: true });
    }
  }

  function loadSlideImage(slide) {
    var img = slide && slide.querySelector(IMAGE_SELECTOR);
    if (!img || !img.dataset.src) return; // no image, or already realized
    img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    // image_tag defaults to loading="lazy" even with no `loading:` param passed;
    // harmless while src was empty, but remove it now that src is real so the
    // browser doesn't defer fetching an image this code just decided to show.
    img.removeAttribute('loading');
  }

  function loadSlidesAround(dialog, index) {
    // The Liquid renders img src/srcset as data-src/data-srcset (see
    // sections/fs-np-masonry.liquid) because the fade effect stacks every
    // slide at the same on-screen position, which defeats native
    // loading="lazy" (the browser sees all 58 as "in viewport" and fetches
    // them at once). This realizes only the active slide ± LAZY_RADIUS,
    // wrapping both directions to match Swiper's rewind.
    var slides = dialog.querySelectorAll(SLIDE_SELECTOR);
    var total = slides.length;
    if (!total) return;
    for (var offset = -LAZY_RADIUS; offset <= LAZY_RADIUS; offset++) {
      var i = ((index + offset) % total + total) % total;
      loadSlideImage(slides[i]);
    }
  }

  function openModal(dialog, index) {
    // Show first: Swiper can only measure slide widths once the dialog is visible
    // (a closed, display:none dialog reports zero client width).
    dialog.showModal();
    setActiveSlide(dialog, index);
    loadSlidesAround(dialog, index);

    var instance = dialog[SWIPER_INSTANCE_KEY];
    if (instance) {
      // Recalc cached dimensions before jumping: while the dialog was closed
      // (display:none) Swiper's ResizeObserver can't see viewport/rotation
      // changes, so a stale layout would misplace slideTo. Mirrors fs-np-hero.js's
      // explicit update() discipline; update() is idempotent and cheap.
      instance.update();
      instance.slideTo(index, 0);
      return;
    }
    if (typeof window.Swiper !== 'function') return; // graceful fallback (CSS shows the active slide)

    var swiperEl = dialog.querySelector(MODAL_SWIPER_SELECTOR);
    if (!swiperEl) return;

    // Nav buttons live inside every slide (58 pairs), so initModal delegates their
    // clicks to slidePrev()/slideNext() rather than binding Swiper's Navigation
    // module; that also stops the A11y module overwriting the localized aria-labels.
    instance = new window.Swiper(swiperEl, {
      initialSlide: index,
      // fade (not slide): the nav buttons live inside each slide (caption-row),
      // so the slide effect's translateX dragged them along with the photo.
      // Mirrors fs-np-hero.js's fade config; unlike the hero, touch stays on
      // (this IS a swipe gallery) so allowTouchMove is left at its true default.
      effect: 'fade',
      fadeEffect: { crossFade: true },
      // rewind (not loop): mirrors fs-np-hero.js — loop needs a duplicate buffer
      // that fights initialSlide and merchant block add/remove.
      rewind: true,
      autoHeight: true, // photos differ in aspect ratio, like fs-np-hero.js
      // Swiper's default 300ms slide transition plays on every keyboard/swipe/
      // button navigation; skip it under reduced motion (matches this file's
      // dialog-open animation, which is likewise gated in fs-np.css).
      speed: prefersReducedMotion() ? 0 : 300,
      // onlyInViewport defaults true, so a closed (display:none) dialog's arrow
      // keys stay inert until it is opened.
      keyboard: { enabled: true },
      a11y: { enabled: true },
      on: {
        // Keyboard/swipe/button navigation moves the active slide without going
        // through openModal, so inert and the lazy-load window must both be
        // resynced here too (see setActiveSlide / loadSlidesAround).
        slideChange: function () {
          setActiveSlide(dialog, this.activeIndex);
          loadSlidesAround(dialog, this.activeIndex);
        },
      },
    });
    dialog[SWIPER_INSTANCE_KEY] = instance;
  }

  function destroySwiper(dialog) {
    if (dialog && dialog[SWIPER_INSTANCE_KEY]) {
      dialog[SWIPER_INSTANCE_KEY].destroy(true, true);
      dialog[SWIPER_INSTANCE_KEY] = null;
    }
  }

  function initModal(section) {
    if (!section || section[MODAL_BOUND_KEY]) return;
    var dialog = section.querySelector(MODAL_SELECTOR);
    // Graceful fallback: without <dialog>/showModal support the triggers are
    // inert (the photo stays visible in the grid, just not enlargeable).
    if (!dialog || typeof dialog.showModal !== 'function') return;
    section[MODAL_BOUND_KEY] = true;

    // Delegated on the section so every trigger works and it survives Masonry
    // re-layout (which repositions, never re-creates, the trigger buttons).
    section.addEventListener('click', function (event) {
      var trigger = event.target.closest(TRIGGER_SELECTOR);
      if (!trigger || !section.contains(trigger)) return;
      // Triggers and slides come from the same photo-block loop in the same
      // order, so the trigger's position IS its slide index.
      var triggers = section.querySelectorAll(TRIGGER_SELECTOR);
      var index = Array.prototype.indexOf.call(triggers, trigger);
      if (index < 0) return;
      openModal(dialog, index);
    });

    // Close: button + backdrop click. Esc is native to showModal().
    var closeBtn = dialog.querySelector('[data-fs-np-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        dialog.close();
      });
    }
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        dialog.close(); // click landed on the backdrop
        return;
      }
      var instance = dialog[SWIPER_INSTANCE_KEY];
      if (!instance) return;
      if (event.target.closest(PREV_SELECTOR)) {
        instance.slidePrev();
      } else if (event.target.closest(NEXT_SELECTOR)) {
        instance.slideNext();
      }
    });
  }

  function initAll(root) {
    forEachGrid(root || document, initGrid);
    forEachSection(root || document, initModal);
  }

  // Script is deferred, so the DOM is parsed by the time this runs.
  initAll(document);

  // Shopify theme editor: keep the layout live as the merchant edits blocks.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    forEachGrid(event.target, destroyGrid);
    // Tear down the lightbox Swiper with the section (like fs-np-hero.js's
    // destroyHero) so its global keyboard/resize listeners don't outlive it.
    forEachSection(event.target, function (section) {
      destroySwiper(section.querySelector(MODAL_SELECTOR));
    });
  });

  document.addEventListener('shopify:section:reorder', function (event) {
    forEachGrid(event.target, layoutGrid);
  });

  // Block events fire on the photo tile (.fs-np-masonry__item), a DESCENDANT of
  // the grid, so walk up with closest() rather than searching inside the target
  // (forEachGrid would never reach the ancestor grid). Mirrors fs-np-hero.js.
  document.addEventListener('shopify:block:select', function (event) {
    var grid = event.target.closest && event.target.closest(GRID_SELECTOR);
    if (grid) layoutGrid(grid);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var grid = event.target.closest && event.target.closest(GRID_SELECTOR);
    if (grid) layoutGrid(grid);
  });
})();
